/** Controls service client — screen-time policy, app rules, schedules, usage. */
import { api } from './client';

export interface Policy {
  id: string;
  child_id: string;
  family_id: string;
  daily_limit_minutes: number | null;
  weekly_limit_minutes: number | null;
  emergency_apps_passthrough: boolean;
  updated_at: string;
}

export interface AppRule {
  id: string;
  child_id: string;
  app_id: string;
  label: string | null;
  action: 'allow' | 'block';
  daily_limit_minutes: number | null;
  updated_at: string;
}

export type Mode = 'bedtime' | 'school' | 'focus' | 'pause';

export interface ModeSchedule {
  id: string;
  child_id: string;
  mode: Mode;
  days_of_week: number;
  start_time: string;
  end_time: string;
  enabled: boolean;
  updated_at: string;
}

export interface DailyUsageRow {
  day: string;
  seconds: number;
}

export interface AppUsageRow {
  app_id: string;
  seconds: number;
}

export interface FocusSession {
  id: string;
  family_id: string;
  child_id: string;
  status: 'active' | 'completed' | 'cancelled';
  label: string | null;
  duration_minutes: number;
  focus_block_minutes: number;
  break_minutes: number;
  allowed_app_ids: string[];
  allow_educational_web: boolean;
  allowed_web_domains: string[];
  distractions_blocked: number;
  started_at: string;
  planned_end_at: string;
  ended_at: string | null;
}

export interface FocusReport {
  session_id: string;
  child_id: string;
  completed: boolean;
  duration_minutes: number;
  focused_minutes: number;
  distractions_blocked: number;
  summary: string;
}

export interface FocusStartInput {
  duration_minutes: number;
  allowed_app_ids?: string[];
  label?: string;
  focus_block_minutes?: number;
  break_minutes?: number;
  allow_educational_web?: boolean;
  allowed_web_domains?: string[];
}

export interface FocusException {
  id: string;
  child_id: string;
  session_id: string;
  app_id: string;
  label: string | null;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  decided_at: string | null;
}

export type CommandKind =
  | 'pause'
  | 'resume'
  | 'lock'
  | 'unlock'
  | 'unlock_temp'
  | 'loud_alert';

export interface DeviceCommand {
  id: string;
  child_id: string;
  kind: CommandKind;
  duration_seconds: number | null;
  note: string | null;
  issued_by_user_id: string;
  issued_at: string;
  ack_at: string | null;
  ack_by_device_id: string | null;
}

export const controlsApi = {
  getPolicy: (childId: string) =>
    api<Policy>(`/v1/controls/children/${childId}/policy`),
  updatePolicy: (childId: string, input: Partial<Pick<Policy, 'daily_limit_minutes' | 'weekly_limit_minutes' | 'emergency_apps_passthrough'>>) =>
    api<Policy>(`/v1/controls/children/${childId}/policy`, {
      method: 'PUT',
      body: input,
    }),
  listAppRules: (childId: string) =>
    api<AppRule[]>(`/v1/controls/children/${childId}/app-rules`),
  addAppRule: (childId: string, input: { app_id: string; label?: string; action: 'allow' | 'block'; daily_limit_minutes?: number | null }) =>
    api<AppRule>(`/v1/controls/children/${childId}/app-rules`, {
      method: 'POST',
      body: input,
    }),
  updateAppRule: (childId: string, ruleId: string, input: Partial<Pick<AppRule, 'label' | 'action' | 'daily_limit_minutes'>>) =>
    api<AppRule>(`/v1/controls/children/${childId}/app-rules/${ruleId}`, {
      method: 'PATCH',
      body: input,
    }),
  removeAppRule: (childId: string, ruleId: string) =>
    api<void>(`/v1/controls/children/${childId}/app-rules/${ruleId}`, { method: 'DELETE' }),
  listSchedules: (childId: string) =>
    api<ModeSchedule[]>(`/v1/controls/children/${childId}/schedules`),
  upsertSchedule: (childId: string, input: { mode: Mode; days_of_week: number; start_time: string; end_time: string; enabled: boolean }) =>
    api<ModeSchedule>(`/v1/controls/children/${childId}/schedules`, {
      method: 'PUT',
      body: input,
    }),
  removeSchedule: (childId: string, mode: Mode) =>
    api<void>(`/v1/controls/children/${childId}/schedules/${mode}`, { method: 'DELETE' }),
  usageDaily: (childId: string, days = 14) =>
    api<DailyUsageRow[]>(`/v1/controls/children/${childId}/usage/daily?days=${days}`),
  usageApps: (childId: string, days = 7, limit = 10) =>
    api<AppUsageRow[]>(`/v1/controls/children/${childId}/usage/apps?days=${days}&limit=${limit}`),
  issueCommand: (
    childId: string,
    input: { kind: CommandKind; duration_seconds?: number; note?: string },
  ) =>
    api<DeviceCommand>(`/v1/controls/children/${childId}/commands`, {
      method: 'POST',
      body: input,
    }),
  // ── Homework / Focus Mode (§22.10) ──
  startFocus: (childId: string, input: FocusStartInput) =>
    api<FocusSession>(`/v1/controls/children/${childId}/focus/start`, {
      method: 'POST',
      body: input,
    }),
  activeFocus: (childId: string) =>
    api<FocusSession | null>(`/v1/controls/children/${childId}/focus/active`),
  endFocus: (childId: string) =>
    api<FocusReport>(`/v1/controls/children/${childId}/focus/end`, { method: 'POST' }),
  focusHistory: (childId: string, limit = 20) =>
    api<FocusSession[]>(`/v1/controls/children/${childId}/focus/history?limit=${limit}`),
  focusExceptions: (childId: string) =>
    api<FocusException[]>(`/v1/controls/children/${childId}/focus/exceptions`),
  decideFocusException: (childId: string, exceptionId: string, approve: boolean) =>
    api<FocusException>(
      `/v1/controls/children/${childId}/focus/exceptions/${exceptionId}/decide`,
      { method: 'POST', body: { approve } },
    ),
};
